import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, CheckCircle2, ArrowRight, Users, Trophy, ShoppingBag, CalendarDays, Store, QrCode, BarChart3, Smartphone, Layers } from 'lucide-react';
import CorporateNavbar from '@/components/CorporateNavbar';
import CorporateFooter from '@/components/CorporateFooter';
import LightPageHero from '@/components/LightPageHero';

const problems = [
  { icon: AlertCircle, text: 'Swish without structure — no clear picture of sales' },
  { icon: AlertCircle, text: 'No overview per register or sales point' },
  { icon: AlertCircle, text: 'Multiple sales points at the same event creates chaos' },
];

const solutions = [
  { icon: BarChart3,    text: 'Sales per kiosk — see what each register sells in real time' },
  { icon: Layers,       text: 'Automatic summaries per register and event' },
  { icon: CheckCircle2, text: 'Export to Excel with one click after the event' },
  { icon: QrCode,       text: 'Unique Swish QR per register — always the right total' },
  { icon: Smartphone,   text: 'Works on mobile — no extra hardware needed' },
  { icon: CheckCircle2, text: 'Automatic receipts to buyers' },
];

const audiences = [
  { icon: Users,        label: 'Sports clubs',     desc: 'Club treasury, cups, and season fees.' },
  { icon: Trophy,       label: 'Tournaments',      desc: 'Entry fees, raffles, and on-site kiosk.' },
  { icon: ShoppingBag,  label: 'Kiosks',            desc: 'Take payments fast without a terminal.' },
  { icon: CalendarDays, label: 'Events & parties',  desc: 'Tickets, bar, and programme fees.' },
  { icon: Store,        label: 'Market stalls',     desc: 'Simple POS for every stall.' },
];

const EnSwishkassaPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <CorporateNavbar />

      <LightPageHero
        badge="Swishkassa · Coming Soon"
        heading={<>Simple payment system for<br /><span style={{ background: 'linear-gradient(135deg, #34d399 0%, #059669 50%, #0d9488 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>events and organizations</span></>}
        subtext="Multiple sales points. Clear overview. No expensive hardware — just Swish QR and a phone."
        ctaLabel="Register interest"
        ctaHref="/contact"
        secondaryLabel="Swedish version"
        secondaryHref="/swishkassa"
      />

      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-emerald-600 mb-4">The problem</span>
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight">Swish works — but lacks a cash register</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {problems.map(({ icon: Icon, text }, i) => (
              <motion.div key={text} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }} className="flex gap-4 items-start bg-red-50 rounded-2xl border border-red-100 p-6">
                <Icon className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                <p className="text-[15px] text-text-primary font-medium leading-snug">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-surface-subtle border-t border-border-light">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-emerald-600 mb-4">The solution</span>
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight">See exactly what each kiosk sells in real time</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {solutions.map(({ icon: Icon, text }, i) => (
              <motion.div key={text} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }} className="flex gap-4 items-start bg-white rounded-2xl border border-border-light p-6 shadow-sm hover:border-emerald-200 transition-colors">
                <Icon className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                <p className="text-[15px] text-text-primary font-medium leading-snug">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white border-t border-border-light">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-emerald-600 mb-4">Who is it for?</span>
            <h2 className="text-3xl font-extrabold text-text-primary tracking-tight">Who needs Swishkassa?</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 max-w-4xl mx-auto">
            {audiences.map(({ icon: Icon, label, desc }) => (
              <motion.div key={label} whileHover={{ y: -4 }} className="bg-white rounded-2xl border border-border-light p-5 text-center shadow-sm hover:shadow-lg hover:border-emerald-200 transition-all duration-300">
                <div className="inline-flex w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 items-center justify-center mb-3 shadow-md">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-[13px] font-bold text-text-primary mb-1">{label}</h3>
                <p className="text-[11px] text-text-secondary leading-snug">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-surface-subtle border-t border-border-light">
        <div className="container mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 mb-6">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse inline-block" />
            In development — launch planned 2025
          </div>
          <h2 className="text-3xl font-extrabold text-text-primary mb-4">Want to be first in line?</h2>
          <p className="text-text-secondary mb-8 max-w-md mx-auto">Register your interest and we'll reach out when Swishkassa is ready for beta testers.</p>
          <Link to="/en/contact">
            <button className="group relative overflow-hidden bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold px-8 py-3.5 rounded-xl text-sm inline-flex items-center gap-2 shadow-lg shadow-emerald-500/25">
              Get early access
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </button>
          </Link>
        </div>
      </section>

      <CorporateFooter />
    </div>
  );
};

export default EnSwishkassaPage;
